import { FC, useMemo, useState } from 'react'
import { ValidationModal } from '../../../../shared/modal/ValidationModal/ValidationModal.component'
import styles from './InsiReviewAndValidateModal.module.scss'
import type { InsiReviewAndValidateModalProps } from './InsiReviewAndValidateModal.model'
import {
  castValue,
  extractValueWithStatus,
  hasDifferencesOnKeys,
} from '../../../../../misc/objects.utilities'
import { Input, SelectInput } from '../../../../shared'
import type { AssigningAuthority } from '../../../../../model/Patient'
import { ComparisonStatus } from '../../../../../model/ComparisonStatus.enum'
import { SelectOption } from '../../../../../model/SelectOption'
import { computeFirstNames } from '../../PatientForm.utils'
import { formatNamesList } from '../IdentityStatusBlock.utils'
import { FIELDS_TO_COMPARE } from '../../../../../features/ins/constants/insFieldsToCompare.constant'

const LABELS = {
  birthLastName: 'Nom de naissance',
  birthFirstName: 'Premier Prénom de naissance',
  otherFirstNames: 'Prénoms de naissance',
  birthDate: 'Date de naissance',
  birthPlaceCode: 'Code de lieu de naissance (INSEE)',
  inseeNumber: 'Matricule INS',
  assigningAuthority: 'OID',
}

const TITLES = {
  withDifferences: 'Différences trouvées',
  noDifferences: 'Aucune différence détectée',
}

const EMPTY_VALUE_LABEL = '(vide)'

export const InsiReviewAndValidateModal: FC<InsiReviewAndValidateModalProps> = ({
  comparisonResults,
  isUnmounting,
  onSubmit,
  onClose,
}) => {
  const { hasDifferences, insiData, patientData } = useMemo(
    () => ({
      hasDifferences: hasDifferencesOnKeys(comparisonResults, FIELDS_TO_COMPARE),
      insiData: extractValueWithStatus(comparisonResults, 'valueA'),
      patientData: extractValueWithStatus(comparisonResults, 'valueB'),
    }),
    [comparisonResults],
  )

  const firstNamesOptions = useMemo(() => {
    const namesValue =
      (insiData?.otherFirstNames?.value as string) ??
      (insiData?.birthFirstName?.value as string) ??
      ''
    const rawFirstNamesOption = computeFirstNames(namesValue)
    return formatNamesList(rawFirstNamesOption).map((value: string) => ({
      label: value,
      name: value,
      value: value,
    }))
  }, [insiData?.otherFirstNames?.value, insiData?.birthFirstName?.value])

  const [selectedFirstName, setSelectedFirstName] = useState<string>(
    () => firstNamesOptions[0]?.value ?? '',
  )

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(selectedFirstName)
    }
  }

  const resolveInputStyle = (status: ComparisonStatus) =>
    status === ComparisonStatus.Identical ? styles.inputValid : styles.inputInvalid

  const renderTitle = (label: string) => (
    <div className={styles.sectionTitleWrapper}>
      <span className={styles.sectionTitle}>{label}</span>
    </div>
  )

  const getFirstNameStatus = () => {
    return (patientData?.birthFirstName?.value as any as string) === selectedFirstName.toLowerCase()
      ? ComparisonStatus.Identical
      : ComparisonStatus.Different
  }

  const formatValue = (value?: string) => (!value?.trim() ? EMPTY_VALUE_LABEL : value)

  const renderInput = (label: string, name: string, value?: string, status?: ComparisonStatus) => (
    <div className={styles.inputWrapper}>
      <Input
        label={label}
        name={name}
        value={formatValue(value)}
        type="text"
        disabled
        colorPreset="light"
        customClassName={status ? resolveInputStyle(status) : undefined}
      />
    </div>
  )

  const renderSelectSearch = (label: string, options: SelectOption<string>[], value?: string) => {
    const selectedOption = options.find((opt) => opt.value === value)
    return (
      <div className={styles.selectWrapper}>
        <SelectInput
          title={label}
          placeholder="Rechercher ou sélectionner un prénom"
          options={options}
          value={selectedOption}
          onSelect={(option) => {
            if (option) {
              setSelectedFirstName(option.value)
            }
          }}
          testId="select_search-first_names"
          colorPreset="light"
        />
      </div>
    )
  }
  const renderOid = (data?: AssigningAuthority, status?: ComparisonStatus) => (
    <div className={styles.inputWrapper}>
      <Input
        label={LABELS.assigningAuthority}
        name="oid"
        value={formatValue(`${data?.kind || ''}${data?.oid ? ` (${data.oid})` : ''}`)}
        type="text"
        disabled
        colorPreset="light"
        customClassName={status ? resolveInputStyle(status) : undefined}
      />
    </div>
  )
  return (
    <ValidationModal
      isUnmounting={isUnmounting}
      onClose={onClose}
      cancelLabel={hasDifferences ? 'Ignorer' : 'Fermer'}
      onSubmit={handleSubmit}
      confirmLabel={hasDifferences ? 'Modifier' : 'Valider'}
      confirmTheme={hasDifferences ? 'invalid' : 'secondary'}
      size="large"
      title={hasDifferences ? TITLES.withDifferences : TITLES.noDifferences}
      icon={hasDifferences ? 'infoCircle' : 'checkCircle'}
      testId="modal-insi_data-comparaison-difference-update"
    >
      <div className={styles.wrapperModal}>
        <div className={styles.grid}>
          <div className={styles.block}>{renderTitle('Patient Follow')}</div>
          <div className={styles.block}>{renderTitle('Retour INSi')}</div>
        </div>

        <div className={styles.grid}>
          <div className={styles.block}>
            {renderInput(
              LABELS.birthLastName,
              'birthLastName',
              castValue<string>(patientData?.birthLastName?.value),
              patientData?.birthLastName?.status,
            )}
            {renderInput(
              LABELS.birthFirstName,
              'birthFirstName',
              castValue<string>(patientData?.birthFirstName?.value),
              getFirstNameStatus(),
            )}
            {renderInput(
              LABELS.otherFirstNames,
              'otherFirstNames',
              castValue<string>(patientData?.otherFirstNames?.value),
              patientData?.otherFirstNames?.status,
            )}
            {renderInput(
              LABELS.birthDate,
              'birthDate',
              castValue<string>(patientData?.birthDate?.value),
              patientData?.birthDate?.status,
            )}
            {renderInput(
              LABELS.birthPlaceCode,
              'birthPlaceCode',
              castValue<string>(patientData?.birthPlaceCode?.value),
              patientData?.birthPlaceCode?.status,
            )}
            {renderInput(
              LABELS.inseeNumber,
              'inseeNumber',
              castValue<string>(patientData?.inseeNumber?.value),
              patientData?.inseeNumber?.status,
            )}
            {renderOid(
              castValue<AssigningAuthority>(patientData?.assigningAuthority?.value),
              patientData?.assigningAuthority?.status,
            )}
          </div>

          <div className={styles.block}>
            {renderInput(
              LABELS.birthLastName,
              'birthLastName',
              castValue<string>(insiData?.birthLastName?.value),
            )}
            {renderSelectSearch(LABELS.birthFirstName, firstNamesOptions, selectedFirstName)}
            {renderInput(
              LABELS.otherFirstNames,
              'otherFirstNames',
              castValue<string>(insiData?.otherFirstNames?.value),
            )}
            {renderInput(
              LABELS.birthDate,
              'birthDate',
              castValue<string>(insiData?.birthDate?.value),
            )}
            {renderInput(
              LABELS.birthPlaceCode,
              'birthPlaceCode',
              castValue<string>(insiData?.birthPlaceCode?.value),
            )}
            {renderInput(
              LABELS.inseeNumber,
              'inseeNumber',
              castValue<string>(insiData?.inseeNumber?.value),
            )}
            {renderOid(castValue<AssigningAuthority>(insiData?.assigningAuthority?.value))}
          </div>
        </div>
      </div>
    </ValidationModal>
  )
}
